import React from 'react';
import PropTypes from 'prop-types';
import isRequiredIf from '../../utils/isRequiredIf';
import Checkbox from './Checkbox';
import Textbox from './Textbox';
import Icon from '@cvent/nucleus-icon';
import { select, resolve } from '@cvent/nucleus-dynamic-css';
import { tapOrClick } from '../../touchEventHandlers';
import removeKeys from '../../utils/removeKeys';

/**
* Removes any MultiSelectDropDownMenu specific properties from a props object that would be invalid
* if applied to a base DOM node. Useful when composing multiple components to prevent
* passing a component props that it doesn't use.
*/
export function removeNonStandardProps(props) {
  return removeKeys(props, ['options', 'fieldName', 'enableFilter', 'filterFunction', 'filterLabel',
    'filterPlaceholderText', 'enableSelectAll', 'selectAllText', 'enableDeselectAll', 'deselectAllText',
    'onNativeChange', 'selectedValues', 'classes', 'inlineLabel', 'inlineElement']);
}

/**
MultiSelectMenu Component

onChange = function(fieldName, newValue, newOptions)
**/

function getNewSelectedValues(selectedValues, value) {
  const newSelectedValues = selectedValues.slice(0);
  const indexInSelectedValues = selectedValues.indexOf(value);
  if (indexInSelectedValues !== -1) {
    newSelectedValues.splice(indexInSelectedValues, 1);
  } else {
    newSelectedValues.push(value);
  }
  return newSelectedValues;
}

export default class MultiSelectDropDownMenu extends React.Component {
  static displayName = 'MultiSelectDropDownMenu';
  static propTypes = {
    /** Options for the multi select */
    options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
    /** HTML ID for the select list. Uses fieldName if ommitted. */
    id: PropTypes.string,
    /** HTML name for the select list. Uses fieldName if ommitted. */
    name: PropTypes.string,
    /** Used by onChange callback to identify which input is changed. */
    fieldName: PropTypes.string.isRequired,
    /** Whether to show the filter box */
    enableFilter: PropTypes.bool,
    /**
      Function to use for filtering options if you dont want to use the default
      case insensitive text filtering. Should return an array in the same form as the input options parameter.
    */
    filterFunction: PropTypes.func,
    /** Text label for the filter box */
    filterLabel: PropTypes.string,
    /** Placeholder text for the filter box */
    filterPlaceholderText: PropTypes.string,
    /** Whether to show the select all button */
    enableSelectAll: PropTypes.bool,
    /** Text to show for the select all button */
    selectAllText: PropTypes.string,
    /** Whether to show the deselect all button */
    enableDeselectAll: PropTypes.bool,
    /** Text to show for the deselect all button */
    deselectAllText: PropTypes.string,
    /** When to break down to 100% width */
    breakPoint: PropTypes.oneOf(['mediaSmall', 'mediaMedium', 'mediaLarge', 'mediaAll']),
    /** Callback Function. Should be implemented per form element. Should exist if onNativeChange is not provided. */
    onChange: isRequiredIf(PropTypes.func, (props) => !props.hasOwnProperty('onNativeChange')),
    /** Callback Function for react-redux-form support. Should exist if onChange is not provided. */
    onNativeChange: isRequiredIf(PropTypes.func, (props) => !props.hasOwnProperty('onChange')),
    /** for react-redux-form support */
    selectedValues: isRequiredIf(PropTypes.array, (props) => props.hasOwnProperty('onNativeChange')),
    /** Available Style Keys. */
    style: PropTypes.shape({
      /** Style object passed to the Checkbox component. */
      checkbox: PropTypes.object,
      /** Top level style applied to the container. */
      container: PropTypes.object,
      /** Style applied to the menu header. */
      header: PropTypes.object,
      /** Style applied to the menu filter Textbox component. */
      filter: PropTypes.object,
      /** Style applied to the select all link. */
      selectAll: PropTypes.object,
      /** Style applied to the deselect all link. */
      deselectAll: PropTypes.object
    })
  };
  static defaultProps = {
    enableFilter: true,
    enableSelectAll: true,
    enableDeselectAll: true
  };
  constructor(props) {
    super(props);
    this.state = {
      filterText: ''
    };
    this.onChange = this.onChange.bind(this);
    this.onGroupItemChange = this.onGroupItemChange.bind(this);
    this.onFilterChange = this.onFilterChange.bind(this);
    this.getFilteredOptions = this.getFilteredOptions.bind(this);
    this.selectAll = this.selectAll.bind(this);
    this.deselectAll = this.deselectAll.bind(this);
  }
  /**
   * OnChange function for the Checkbox component. Ideally we would just pass the onChange
   * property straight through to the Checkbox, but that will break things if the filtering
   * feature is being used when the onChange fires.
   */
  onChange(fieldName, newValue, newOptions) {
    // Options are not being filtered, so just pass through the normal onChange behavior.
    if (!this.state.filterText) {
      if (this.props.hasOwnProperty('onNativeChange') && this.props.hasOwnProperty('selectedValues')) {
        this.props.onNativeChange(newOptions.selectedValues);
      } else {
        this.props.onChange(fieldName, newValue, newOptions);
      }
      return;
    }
    // This mostly duplicates the Checkbox component's built in onChange function in order to handle
    // changing selections while options are being filtered.
    if (this.props.hasOwnProperty('onNativeChange') && this.props.hasOwnProperty('selectedValues')) {
      this.props.onNativeChange(getNewSelectedValues(this.props.selectedValues, newValue));
      return;
    }
    let newOptionList;
    if (this.props.options.hasOwnProperty('selectedValues')) {
      newOptionList = Object.assign({}, this.props.options,
        { selectedValues: getNewSelectedValues(this.props.options.selectedValues, newValue) });
    } else {
      newOptionList = this.props.options.map((option) => {
        return (option.value !== newValue) ? option :
          Object.assign({}, option, { checked: !option.checked });
      });
    }

    this.props.onChange(fieldName, newValue, newOptionList);
  }

  /**
   * OnChange function for the Checkbox components when the options property is
   * specifying category groups. Each group is created from a separate Checkbox component, so
   * the onChange needs to be customized to fit in with the larger list of options.
   */
  onGroupItemChange(index, fieldName, newValue) {
    const newGrouping = { [index]: Object.assign({}, this.props.options[index],
      { items: this.props.options[index].items.map((item) => {
        return (item.value !== newValue) ? item :
          Object.assign({}, item, { checked: !item.checked });
      }) }) };
    const newOptionList = Object.assign([], this.props.options, newGrouping);
    if (this.props.hasOwnProperty('onNativeChange') && this.props.hasOwnProperty('selectedValues')) {
      this.props.onNativeChange(getNewSelectedValues(this.props.selectedValues, newValue));
    } else {
      this.props.onChange(this.props.fieldName, newValue, newOptionList);
    }
  }

  /**
   * Sets the filter text in the state.
   */
  onFilterChange(fieldName, newValue) {
    this.setState({ filterText: newValue });
  }

  /**
   * Filters the provided options object using the state's filter text.
   */
  getFilteredOptions(options) {
    if (!this.state.filterText) {
      return options;
    }
    if (this.props.filterFunction) {
      return this.props.filterFunction(this.state.filterText, options);
    }
    if (options.hasOwnProperty('selectedValues')) {
      return {
        selectedValues: options.selectedValues,
        optionArray: options.optionArray.filter((option) => {
          return option.filterText ? option.filterText.toUpperCase().includes(this.state.filterText.toUpperCase())
            : option.name.toUpperCase().includes(this.state.filterText.toUpperCase());
        })
      };
    }
    return options.filter((option) => {
      return option.filterText ? option.filterText.toUpperCase().includes(this.state.filterText.toUpperCase())
        : option.name.toUpperCase().includes(this.state.filterText.toUpperCase());
    });
  }


  /**
   * Select all options in the options property.
   */
  selectAll(e) {
    e.preventDefault();
    let newOptions;
    const isNucleusField = this.props.hasOwnProperty('onNativeChange') && this.props.hasOwnProperty('selectedValues');

    if (this.props.options.hasOwnProperty('selectedValues')) {
      const selectedValues = this.props.options.optionArray.map((option) => {
        return !option.disabled || this.props.options.selectedValues.indexOf(option.value) >= 0 ? option.value : null;
      }).filter((option) => option !== null);
      if (isNucleusField) {
        this.props.onNativeChange(selectedValues);
        return;
      }
      newOptions = Object.assign({}, this.props.options, { selectedValues });
    } else if (this.props.options.length && this.props.options[0].hasOwnProperty('category')) {
      const selectedValues = [];
      newOptions = this.props.options.map((category) => {
        return Object.assign({}, category, { items: category.items.map((option) => {
          selectedValues.push(option.value);
          return Object.assign({}, option, option.disabled ? {} : { checked: true });
        }) });
      });
      if (isNucleusField) {
        this.props.onNativeChange(selectedValues);
        return;
      }
    } else {
      const selectedValues = [];
      newOptions = this.props.options.map((option) => {
        selectedValues.push(option.value);
        return Object.assign({}, option, option.disabled ? {} : { checked: true });
      });
      if (isNucleusField) {
        this.props.onNativeChange(selectedValues);
        return;
      }
    }
    this.props.onChange(this.props.fieldName, null, newOptions);
  }

  /**
   * Deselect all options in the options property.
   */
  deselectAll(e) {
    e.preventDefault();
    if (this.props.hasOwnProperty('onNativeChange') && this.props.hasOwnProperty('selectedValues')) {
      this.props.onNativeChange([]);
      return;
    }
    let newOptions;
    if (this.props.options.hasOwnProperty('selectedValues')) {
      const selectedDisabledValues = this.props.options.optionArray.map((option) => {
        return option.disabled && this.props.options.selectedValues.indexOf(option.value) >= 0 ? option.value : null;
      }).filter((option) => option !== null);
      newOptions = Object.assign({}, this.props.options, { selectedValues: selectedDisabledValues });
    } else if (this.props.options.length && this.props.options[0].hasOwnProperty('category')) {
      newOptions = this.props.options.map((category) => {
        return Object.assign({}, category, { items: category.items.map((option) => {
          return Object.assign({}, option, option.disabled ? {} : { checked: false });
        }) });
      });
    } else {
      newOptions = this.props.options.map((option) => {
        return Object.assign({}, option, option.disabled ? {} : { checked: false });
      });
    }
    this.props.onChange(this.props.fieldName, null, newOptions);
  }

  render() {
    let content;
    if (this.props.options.length && this.props.options[0].hasOwnProperty('category')) {
      content = this.props.options.map((category, index) => {
        const filteredOptions = this.getFilteredOptions(category.items);
        if (!filteredOptions.length) {
          return null;
        }
        return (
          <Checkbox {...this.props}
            {...select(this.props, 'checkbox')}
            key={index}
            id={(this.props.id || this.props.fieldName) + index}
            name={(this.props.name || this.props.fieldName) + index}
            fieldName={category.category}
            label={category.category}
            options={filteredOptions}
            onChange={this.onGroupItemChange.bind(null, index)} />
        );
      }).filter((component) => {return component !== null;});
    } else {
      content = (
        <Checkbox {...this.props}
          {...select(this.props, 'checkbox')}
          label=""
          hideLabel
          options={this.getFilteredOptions(this.props.options)}
          onChange={this.onChange}
          children={undefined} />
      );
    }
    const { breakPoint } = this.props;
    const showHeader = this.props.enableFilter || this.props.enableSelectAll || this.props.enableDeselectAll;
    return (
      <div {...resolve(this.props, 'container', breakPoint)} ref="menu">
        { showHeader && <div {...resolve(this.props, 'header')}>
          {this.props.enableFilter ?
            <Textbox {...select(this.props, 'filter')}
              fieldName="filter"
              label={this.props.filterLabel}
              placeholder={this.props.filterPlaceholderText}
              value={this.state.filterText}
              onChange={this.onFilterChange} /> : null}
          {this.props.enableSelectAll ?
            <a href="#" {...resolve(this.props, 'selectAll')}
              {...tapOrClick(this.selectAll)}>
              <Icon icon={'checkFilled'} fallbackText={'Select All'} />
              {this.props.selectAllText}</a> : null}
          {this.props.enableDeselectAll ?
            <a href="#" {...resolve(this.props, 'deselectAll')}
              {...tapOrClick(this.deselectAll)}>
              <Icon icon={'closeDeleteFilled'} fallbackText={'Select All'} />
              {this.props.deselectAllText}</a> : null}
        </div>}
        {content}
      </div>
    );
  }
}

/**
#### Data structures for the "options" property
Data structure for the "options" property mirrors that of the Checkbox form element, but each option has an additional
available property called `filterText` that can be used to change the text the options are filtered by.

If filterText is provided, it will be used as the text for filtering instead of name.
If disabled is set, that option will be disabled in the select.

```js
  [
    {name: 'optionNameA', value: 'optionValueA', checked: true, disabled: true},
    {name: 'optionNameB', value: 'optionValueB', checked: false, filterText: 'optionNameA'},
    ...
  ]
```

```js
  [
    {category: 'categoryNameA', items:
      [
        {name: 'optionNameA', value: 'optionValueA', checked: true},
        ...
      ]
    },
    {category: 'categoryNameB', items:
      [
        {name: 'optionNameB', value: 'optionValueB', checked: false},
        ...
      ]
    }
  ]
```

```js
  {
    selectedValues: ['optionValueA'],
    optionArray: [
      {name: 'optionNameA', value: 'optionValueA'},
      {name: 'optionNameB', value: 'optionValueB'},
      ...
    ]
  }
```
**/
